import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NavigationLoader.module.css';

const MIN_DISPLAY_MS = 520;
const FADE_OUT_MS = 220;

const NavigationLoader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFirstRender = useRef(true);
  const frameRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const unmountTimeoutRef = useRef<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
    if (unmountTimeoutRef.current) window.clearTimeout(unmountTimeoutRef.current);
    if (frameRef.current) window.cancelAnimationFrame(frameRef.current);

    setMounted(true);
    frameRef.current = window.requestAnimationFrame(() => {
      setActive(true);
    });

    hideTimeoutRef.current = window.setTimeout(() => {
      setActive(false);
      unmountTimeoutRef.current = window.setTimeout(() => {
        setMounted(false);
      }, FADE_OUT_MS);
    }, MIN_DISPLAY_MS);
  }, [location.key]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
      if (unmountTimeoutRef.current) window.clearTimeout(unmountTimeoutRef.current);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    };
  }, []);

  if (!mounted) return null;

  return (
    <div className={`${styles.overlay} ${active ? styles.active : ''}`} role="status" aria-live="polite">
      <div className={styles.spinner} aria-hidden="true" />
      <p className={styles.label}>{t('loader.pageLoading')}</p>
    </div>
  );
};

export default NavigationLoader;
